import { Alert, Card, Spin, Tooltip } from 'antd'
import React from 'react'
import { SPARTACUS_STATS_TOOLTIPS } from 'src/constants/spartacusTooltips'
import { getSpartacusLoadingPresentation } from 'src/utils/spartacusLoading'
import { ColumnHeaderWithTooltip } from 'src/utils/venueProductFieldTooltips'
import {
  formatPercentValue,
  formatSignedPercentDelta,
  formatSignedPointsDelta,
} from 'src/utils/spartacusMappers'
import styled from 'styled-components/macro'

function StatTitle({ label, tooltipKey }) {
  return (
    <ColumnHeaderWithTooltip
      label={label}
      tooltip={SPARTACUS_STATS_TOOLTIPS[tooltipKey]}
    />
  )
}

function DeltaText({ value, formatter, priorPeriodTooltip }) {
  const formatted = formatter(value)
  if (!formatted) {
    return null
  }

  const isPositive = formatted.startsWith('+')
  const isNegative = formatted.startsWith('-')

  return (
    <Tooltip title={priorPeriodTooltip || SPARTACUS_STATS_TOOLTIPS.delta}>
      <Delta $positive={isPositive} $negative={isNegative}>
        {formatted} vs prior
      </Delta>
    </Tooltip>
  )
}

function StatCell({
  title,
  value,
  delta,
  formatter,
  footnote,
  priorPeriodTooltip,
}) {
  return (
    <StatItem>
      <StatLabel>{title}</StatLabel>
      <StatValueRow>
        <StatValue>{value}</StatValue>
        {delta != null && formatter ? (
          <DeltaText
            value={delta}
            formatter={formatter}
            priorPeriodTooltip={priorPeriodTooltip}
          />
        ) : null}
      </StatValueRow>
      {footnote ? <StatFootnote>{footnote}</StatFootnote> : null}
    </StatItem>
  )
}

function SpartacusStatsBar({ stats, loading, error, priorPeriodTooltip }) {
  const { showInitialLoader, showRefreshOverlay } =
    getSpartacusLoadingPresentation({
      loading,
      hasData: Boolean(stats),
    })

  if (error && !stats) {
    return (
      <StatsCard title='Stats'>
        <Alert type='error' showIcon message={error} />
      </StatsCard>
    )
  }

  if (showInitialLoader) {
    return (
      <StatsCard title='Stats'>
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      </StatsCard>
    )
  }

  if (!stats) {
    return (
      <StatsCard title='Stats'>
        <EmptyCopy>No stats available for the selected range.</EmptyCopy>
      </StatsCard>
    )
  }

  const retentionTotal =
    (stats.paidDecliningCount || 0) + (stats.freeDecliningCount || 0)

  return (
    <StatsCard
      title={
        <StatsTitleRow>
          <span>Stats</span>
          {showRefreshOverlay ? (
            <RefreshingLabel>Updating…</RefreshingLabel>
          ) : null}
        </StatsTitleRow>
      }
    >
      {error ? <StyledAlert type='error' showIcon message={error} /> : null}
      <StatsContent $refreshing={showRefreshOverlay}>
        <StatsGrid>
          <StatCell
            title={<StatTitle label='Total venues' tooltipKey='totalVenues' />}
            value={stats.totalVenues}
          />
          <StatCell
            title={<StatTitle label='Total orders' tooltipKey='totalOrders' />}
            value={stats.totalOrders}
            delta={stats.deltas?.totalOrdersPct}
            formatter={formatSignedPercentDelta}
            priorPeriodTooltip={priorPeriodTooltip}
          />
          <StatCell
            title={<StatTitle label='Free venues' tooltipKey='freeVenuePct' />}
            value={formatPercentValue(stats.freeVenuePct, 1)}
            delta={stats.deltas?.freeVenuePctChange}
            formatter={formatSignedPointsDelta}
            priorPeriodTooltip={priorPeriodTooltip}
          />
          <StatCell
            title={<StatTitle label='Paid venues' tooltipKey='paidVenuePct' />}
            value={formatPercentValue(stats.paidVenuePct, 1)}
            delta={stats.deltas?.paidVenuePctChange}
            formatter={formatSignedPointsDelta}
            priorPeriodTooltip={priorPeriodTooltip}
          />
          <StatCell
            title={<StatTitle label='Activation' tooltipKey='activationPct' />}
            value={formatPercentValue(stats.activationPct, 1)}
            delta={stats.deltas?.activationPctChange}
            formatter={formatSignedPointsDelta}
            priorPeriodTooltip={priorPeriodTooltip}
          />
          <StatCell
            title={
              <StatTitle label='Active venues' tooltipKey='activeVenueCount' />
            }
            value={stats.activeVenueCount}
            delta={stats.deltas?.activeVenuePctChange}
            formatter={formatSignedPointsDelta}
            priorPeriodTooltip={priorPeriodTooltip}
          />
          <StatCell
            title={
              <StatTitle
                label='Dormant venues'
                tooltipKey='dormantVenueCount'
              />
            }
            value={stats.dormantVenueCount}
          />
          <StatCell
            title={
              <StatTitle
                label='High-intent free'
                tooltipKey='highIntentFreeCount'
              />
            }
            value={stats.highIntentFreeCount}
          />
          <StatCell
            title={
              <StatTitle label='Retention pulse' tooltipKey='retentionPulse' />
            }
            value={retentionTotal}
            footnote={`${stats.paidDecliningCount} paid · ${stats.freeDecliningCount} free`}
          />
        </StatsGrid>
      </StatsContent>
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head {
    min-height: 40px;
    padding: 0 16px;
  }

  .ant-card-head-title {
    font-weight: 600;
    padding: 10px 0;
  }

  .ant-card-body {
    padding: 12px 16px 14px;
  }
`

const StatsTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
  justify-content: space-between;
  width: 100%;
`

const RefreshingLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 12px;
  font-weight: 500;
`

const StyledAlert = styled(Alert)`
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`

const StatsContent = styled.div`
  opacity: ${({ $refreshing }) => ($refreshing ? 0.55 : 1)};
  pointer-events: ${({ $refreshing }) => ($refreshing ? 'none' : 'auto')};
  transition: opacity 0.2s ease;
`

const StatsGrid = styled.div`
  display: grid;
  gap: 10px 20px;
  grid-template-columns: repeat(2, minmax(0, 1fr));

  @media (min-width: 640px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (min-width: 1100px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`

const StatItem = styled.div`
  min-width: 0;
`

const StatLabel = styled.div`
  color: ${({ theme }) => theme.colours.darkGrey};
  font-size: 12px;
  line-height: 1.3;
  margin-bottom: 2px;
`

const StatValueRow = styled.div`
  align-items: baseline;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const StatValue = styled.div`
  color: ${({ theme }) => theme.colours.bbqBlack};
  font-size: 20px;
  font-weight: 600;
  line-height: 1.2;
`

const Delta = styled.span`
  color: ${({ $positive, $negative, theme }) => {
    if ($positive) {
      return theme.colours.lettuceGreen
    }
    if ($negative) {
      return theme.colours.chilliRed
    }
    return theme.colours.oliveGrey
  }};
  cursor: help;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
`

const StatFootnote = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey};
  font-size: 11px;
  line-height: 1.2;
  margin-top: 2px;
`

const LoadingWrap = styled.div`
  display: flex;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.md};
`

const EmptyCopy = styled.p`
  color: ${({ theme }) => theme.colours.darkGrey};
  margin: 0;
`

export default SpartacusStatsBar
